import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

const SOCKET_URL = import.meta.env.VITE_API_URL?.replace('/api/v1', '') || 'http://localhost:4000';

let globalSocket: Socket | null = null;

function getSocket(): Socket {
  const token = sessionStorage.getItem('accessToken');
  if (!globalSocket || !globalSocket.connected) {
    globalSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      auth: { token: token || '' },
    });
  }
  return globalSocket;
}

export function disconnectSocket() {
  if (globalSocket?.connected) {
    globalSocket.disconnect();
  }
  globalSocket = null;
}

type SocketEventHandler = (data: unknown) => void;

/**
 * Hook para escuchar eventos de Socket.IO.
 * @param room  Canal al que suscribirse (e.g. 'project:abc123'). Null para no suscribirse.
 * @param events  Mapa de { eventName: handler }
 */
export function useSocket(
  room: string | null,
  events: Record<string, SocketEventHandler>
) {
  const eventsRef = useRef(events);
  eventsRef.current = events;

  useEffect(() => {
    const socket = getSocket();

    if (room) {
      socket.emit('subscribe', room);
    }

    const handlers: Record<string, SocketEventHandler> = {};
    Object.entries(eventsRef.current).forEach(([event, handler]) => {
      const wrapped: SocketEventHandler = (data) => handler(data);
      handlers[event] = wrapped;
      socket.on(event, wrapped);
    });

    return () => {
      Object.entries(handlers).forEach(([event, handler]) => {
        socket.off(event, handler);
      });
      if (room) {
        socket.emit('unsubscribe', room);
      }
    };
  }, [room]);
}
